'use client';

import type { ReactNode } from 'react';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { DesktopShell } from '@/shared/ui';
import { AppSidebar } from './AppSidebar';

interface DashboardDesktopLayoutProps {
  children: ReactNode;
}

/**
 * Desktop layout wrapper for the Dashboard page.
 *
 * Breakpoint behavior:
 * - < lg (1024px): Mobile layout, renders children directly
 * - >= lg (1024px): 2-column layout (sidebar + content)
 */
export function DashboardDesktopLayout({
  children,
}: DashboardDesktopLayoutProps) {
  const { isLg } = useBreakpoint();
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);

  if (!isLg) {
    return <>{children}</>;
  }

  // Mirror DesktopShell's collapsed LEFT gutter with a RIGHT padding on the
  // scroll container so the page's `mx-auto` content re-centers, while the
  // scrollbar stays pinned to the window edge (padding is inside the border
  // box). See ProjectDesktopLayout for the same pattern. The collapsed state
  // also reveals the floating bottom bar (MobileNavigation, fixed bottom-3), so
  // add a bottom clearance (pb-24) — otherwise the tail of long lists (history,
  // help) scrolls under the bar and can never be fully revealed.
  return (
    <DesktopShell sidebar={<AppSidebar />}>
      <main
        // `scrollbar-gutter: stable` keeps the column's inner width constant
        // whether or not it currently overflows, so content sized from that
        // width can't flip the overflow state back (see ProjectDesktopLayout).
        // `data-page-scroll` marks THE scroller a full-viewport overlay has to
        // hold still: this column scrolls, not the document, so an overlay
        // would otherwise sit next to a live second scrollbar (see
        // `useOverlayScrollLock`).
        data-page-scroll=""
        className={`flex-1 min-w-0 overflow-y-auto [scrollbar-gutter:stable]${sidebarCollapsed ? ' pe-14 pb-24' : ''}`}
      >
        {children}
      </main>
    </DesktopShell>
  );
}
